/**
 * The single place that talks to the network.
 *
 * Every request gets the bearer token attached; a 401 clears the stored token,
 * re-prompts and retries exactly once. Pointing the app at the real server is a
 * config change (see config.ts), not a code change.
 */

import { apiUrl } from "./config";
import { MissingTokenError, refreshTokenAfterRejection, requireToken } from "./auth";

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;

  constructor(status: number, detail: string) {
    super(detail === "" ? `Request failed with status ${status}` : detail);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NetworkError";
  }
}

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

export interface RequestOptions {
  readonly method?: HttpMethod;
  readonly params?: Readonly<Record<string, string>>;
  readonly body?: unknown;
  /** A file to send as-is (streamed, not JSON-encoded); wins over `body`. */
  readonly file?: File;
  readonly signal?: AbortSignal;
}

async function readErrorDetail(response: Response): Promise<string> {
  try {
    const text = await response.text();
    if (text === "") {
      return "";
    }
    try {
      const parsed: unknown = JSON.parse(text);
      if (parsed !== null && typeof parsed === "object") {
        const record = parsed as Record<string, unknown>;
        const message = record["error"] ?? record["message"] ?? record["detail"];
        if (typeof message === "string") {
          return message;
        }
      }
    } catch {
      /* not JSON; fall through to the raw text */
    }
    return text.slice(0, 400);
  } catch {
    return "";
  }
}

async function send(path: string, options: RequestOptions, token: string): Promise<Response> {
  const headers = new Headers({ Accept: "application/json" });
  headers.set("Authorization", `Bearer ${token}`);

  const init: RequestInit = { method: options.method ?? "GET", headers };
  if (options.signal !== undefined) {
    init.signal = options.signal;
  }
  if (options.file !== undefined) {
    // The browser sets Content-Length for a File body, which the server needs.
    headers.set("Content-Type", options.file.type || "application/octet-stream");
    init.body = options.file;
  } else if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    init.body = JSON.stringify(options.body);
  }

  try {
    return await fetch(apiUrl(path, options.params), init);
  } catch (cause: unknown) {
    if (cause instanceof DOMException && cause.name === "AbortError") {
      throw cause;
    }
    throw new NetworkError("Could not reach the vineelernote server.");
  }
}

async function decodeBody<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  if (text.trim() === "") {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

/**
 * Performs an authenticated API call and returns the parsed JSON body.
 * `T` is the *raw* wire shape; normalisation happens in decode.ts.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = await requireToken();
  let response = await send(path, options, token);

  if (response.status === 401) {
    // Stored token is stale or wrong. Drop it, ask once more, retry once.
    const replacement = await refreshTokenAfterRejection();
    response = await send(path, options, replacement);
  }

  if (!response.ok) {
    throw new ApiError(response.status, await readErrorDetail(response));
  }

  return decodeBody<T>(response);
}

/** True when the failure was "the user has no usable token", not a server fault. */
export function isAuthFailure(error: unknown): boolean {
  return error instanceof MissingTokenError || (error instanceof ApiError && error.status === 401);
}
